// Create-campaign form. Wires to the existing POST /api/campaigns endpoint.
// Mounted at /campaigns/new (handled inside the Campaigns route component).

import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, Brand } from '../lib/api';
import { T } from '../theme';

const VERTICALS = [
  'music', 'gaming', 'fashion', 'food', 'sports', 'entertainment', 'general',
];

const GEO_PRESETS: Array<{ id: string; label: string }> = [
  { id: 'GLOBAL',           label: 'Global' },
  { id: '["US"]',           label: 'United States' },
  { id: '["JP"]',           label: 'Japan' },
  { id: '["KR"]',           label: 'Korea' },
  { id: '["TH","ID","PH","VN"]', label: 'SEA (TH · ID · PH · VN)' },
  { id: '["DE","NL","FR","UK"]', label: 'EU (DE · NL · FR · UK)' },
];

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    brand_id: '',
    title: '',
    artist: '',
    track: '',
    brief: '',
    budget_total_dollars: '',
    vertical: 'music',
    geography: 'GLOBAL',
    starts_at: '',
    ends_at: '',
  });

  useEffect(() => {
    api.brands.list()
      .then((res) => {
        setBrands(res.brands);
        if (res.brands.length > 0 && !form.brand_id) {
          setForm((f) => ({ ...f, brand_id: res.brands[0].id }));
        }
      })
      .catch((e) => setError(`Failed to load brands: ${e instanceof Error ? e.message : String(e)}`))
      .finally(() => setBrandsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!form.brand_id || !form.title.trim()) {
      setError('Brand and title are required.');
      return;
    }

    const budgetCents = form.budget_total_dollars
      ? Math.round(parseFloat(form.budget_total_dollars) * 100)
      : 0;

    setSubmitting(true);
    try {
      const res = await api.campaigns.create({
        brand_id: form.brand_id,
        title: form.title.trim(),
        artist: form.artist.trim() || undefined,
        track: form.track.trim() || undefined,
        brief: form.brief.trim() || undefined,
        budget_total: budgetCents || undefined,
        vertical: form.vertical || undefined,
        geography: form.geography || undefined,
        starts_at: form.starts_at || undefined,
        ends_at: form.ends_at || undefined,
      });
      navigate(`/campaigns/${res.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  return (
    <>
      <Link to="/operator/campaigns" style={createStyles.backLink}>
        <span style={{ marginRight: 8 }}>←</span> Cancel
      </Link>

      <div style={createStyles.header}>
        <p style={createStyles.eyebrow}>New campaign</p>
        <h1 style={createStyles.title}>Brief a new campaign.</h1>
        <p style={createStyles.subtitle}>
          A campaign starts as a draft. Vendor allocation and approval happen on the detail page after creation.
        </p>
      </div>

      {error && (
        <div style={createStyles.error}>
          <div style={{ color: T.error, fontFamily: T.font.mono, fontStyle: 'normal' }}>
            {error}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={createStyles.form}>
        {/* Brand */}
        <Field label="Brand" required>
          <select
            value={form.brand_id}
            onChange={(e) => update('brand_id', e.target.value)}
            disabled={brandsLoading || submitting}
            style={createStyles.input}
            required
          >
            {brandsLoading && <option>Loading brands…</option>}
            {!brandsLoading && brands.length === 0 && <option value="">No brands available</option>}
            {!brandsLoading && brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </Field>

        {/* Title */}
        <Field label="Title" required hint="A name the team will recognize. e.g. 'Push Just Like Dat SEA — June'">
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            disabled={submitting}
            style={createStyles.input}
            required
            maxLength={200}
          />
        </Field>

        {/* Artist + Track */}
        <div style={createStyles.row2}>
          <Field label="Artist" hint="Music vertical only.">
            <input
              type="text"
              value={form.artist}
              onChange={(e) => update('artist', e.target.value)}
              disabled={submitting}
              style={createStyles.input}
              maxLength={200}
            />
          </Field>
          <Field label="Track" hint="Music vertical only.">
            <input
              type="text"
              value={form.track}
              onChange={(e) => update('track', e.target.value)}
              disabled={submitting}
              style={createStyles.input}
              maxLength={200}
            />
          </Field>
        </div>

        {/* Brief */}
        <Field label="Brief" hint="What is this campaign trying to achieve? Audience, intent, anchor moments.">
          <textarea
            value={form.brief}
            onChange={(e) => update('brief', e.target.value)}
            disabled={submitting}
            style={{ ...createStyles.input, minHeight: 120, resize: 'vertical' as const, fontFamily: T.font.body }}
            maxLength={2000}
            rows={5}
          />
        </Field>

        {/* Budget + Vertical */}
        <div style={createStyles.row2}>
          <Field label="Budget total (USD)" hint="Total ceiling, before vendor allocation.">
            <input
              type="number"
              value={form.budget_total_dollars}
              onChange={(e) => update('budget_total_dollars', e.target.value)}
              disabled={submitting}
              style={createStyles.input}
              min="0"
              step="1"
              placeholder="40000"
            />
          </Field>
          <Field label="Vertical">
            <select
              value={form.vertical}
              onChange={(e) => update('vertical', e.target.value)}
              disabled={submitting}
              style={createStyles.input}
            >
              {VERTICALS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* Geography */}
        <Field label="Geography" hint="Targeted regions.">
          <select
            value={form.geography}
            onChange={(e) => update('geography', e.target.value)}
            disabled={submitting}
            style={createStyles.input}
          >
            {GEO_PRESETS.map((g) => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
        </Field>

        {/* Window */}
        <div style={createStyles.row2}>
          <Field label="Start date">
            <input
              type="date"
              value={form.starts_at}
              onChange={(e) => update('starts_at', e.target.value)}
              disabled={submitting}
              style={createStyles.input}
            />
          </Field>
          <Field label="End date">
            <input
              type="date"
              value={form.ends_at}
              onChange={(e) => update('ends_at', e.target.value)}
              disabled={submitting}
              style={createStyles.input}
            />
          </Field>
        </div>

        {/* Submit */}
        <div style={createStyles.actions}>
          <button
            type="submit"
            disabled={submitting || brandsLoading || !form.brand_id || !form.title.trim()}
            style={{ ...createStyles.submit, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'wait' : 'pointer' }}
          >
            {submitting ? 'Creating…' : 'Create draft'}
          </button>
          <Link to="/operator/campaigns" style={createStyles.cancel}>Cancel</Link>
        </div>
      </form>
    </>
  );
}

function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={createStyles.field}>
      <label style={createStyles.label}>
        {label}
        {required && <span style={createStyles.required}> · required</span>}
      </label>
      {children}
      {hint && <div style={createStyles.hint}>{hint}</div>}
    </div>
  );
}

const createStyles: Record<string, React.CSSProperties> = {
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 13,
    color: T.inkSoft,
    letterSpacing: '0.06em',
    textDecoration: 'none',
    marginBottom: 32,
    padding: '6px 0',
  },
  header: {
    paddingBottom: 24,
    borderBottom: `1px solid ${T.rule}`,
    marginBottom: 40,
  },
  eyebrow: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase' as const,
    color: T.accent,
    marginBottom: 12,
  },
  title: {
    fontFamily: T.font.display,
    fontWeight: 400,
    fontSize: 40,
    color: T.ink,
    letterSpacing: '0.012em',
    lineHeight: 1.1,
    marginBottom: 12,
  },
  subtitle: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 15,
    color: T.inkSoft,
    maxWidth: 640,
    lineHeight: 1.6,
  },
  error: {
    padding: 18,
    border: `1px solid ${T.error}`,
    background: T.errorSoft,
    marginBottom: 24,
  },
  form: {
    maxWidth: 720,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 24,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  label: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: T.inkSoft,
  },
  required: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 10,
    color: T.accent,
  },
  hint: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 12,
    color: T.inkMute,
    lineHeight: 1.5,
  },
  input: {
    background: T.bgSoft,
    color: T.ink,
    border: `1px solid ${T.rule}`,
    padding: '12px 14px',
    fontSize: 14,
    fontFamily: T.font.body,
    borderRadius: 0,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    marginTop: 12,
    paddingTop: 24,
    borderTop: `1px solid ${T.rule}`,
  },
  submit: {
    background: T.accent,
    color: T.bg,
    border: `1px solid ${T.accent}`,
    fontFamily: T.font.body,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    padding: '14px 32px',
    transition: 'background 0.2s, transform 0.2s',
  },
  cancel: {
    fontFamily: T.font.mono,
    fontStyle: 'normal',
    fontSize: 13,
    color: T.inkSoft,
    textDecoration: 'none',
    letterSpacing: '0.06em',
  },
};
